import { getAuthToken } from "@/dataProvider/axios";
import env from "@/config/env";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { getDefaultOrgId } from "@/utils/defaultOrg";
import { friendlyHttpMessage, httpError, notifyBudgetExceeded } from "@/lib/http/apiError";

const cleanBaseUrl = (value: string): string => {
  let end = value.length;
  while (end > 0 && value[end - 1] === "/") end -= 1;
  return value.slice(0, end);
};

const DEFAULT_BASE_URL = `${cleanBaseUrl(env.API_URL)}/proxy/debugging`;

export const debuggingBaseUrl =
  (typeof window !== "undefined" &&
    (window as Window & { __NCODER_DEBUGGING_URL__?: string }).__NCODER_DEBUGGING_URL__) ||
  DEFAULT_BASE_URL;

export type DebugRunStatus = "queued" | "running" | "done" | "failed" | "cancelled" | string;

export interface DebugRunSnapshot {
  run_id: string;
  org_id: string;
  user_id: string;
  kind: string;
  status: DebugRunStatus;
  stage: string;
  progress: number;
  created_at: string;
  updated_at: string;
  error?: string | null;
  result?: DebugAnalysis | null;
}

/**
 * Analysis verdict. `healthy` is reachable ONLY when every requested log was fetched
 * and parsed cleanly and none contained an error. `unknown` (nothing parsed) and
 * `parse_failed` (parsed, but parse health is not ok) both mean "we could not
 * determine" — they must never render as a clean bill of health, nor as a hard error.
 */
export type DebugVerdict = "healthy" | "failing" | "unknown" | "parse_failed";

/** Aggregate parse-health status across the analyzed logs. */
export type ParseHealthStatus = "ok" | "degraded" | "failed";

/**
 * Report-level parse health. Note `summary.parse_health` is only the status STRING —
 * the full object lives at the top level and is mirrored at `diagnosis.parse_health`.
 */
export interface DebugParseHealth {
  status: ParseHealthStatus;
  ok: boolean;
  logs_requested: number;
  logs_analyzed: number;
  logs_failed: number;
  logs_missing: string[];
  logs_with_unparsed_lines: string[];
  unparsed_line_count: number;
  failures: Array<{ log_id?: string; error?: string }>;
  /** Human-readable sentences explaining why parsing was degraded or failed. */
  reasons: string[];
}

export interface DebugRootCause {
  headline?: string;
  severity?: "error" | "warning" | "info" | string;
  confidence?: "high" | "medium" | "low" | string;
  what_broke?: string;
  why?: string;
  where?: { component?: string | null; line?: number | null; snippet?: string };
  fix?: { summary?: string; language?: string; patch?: string; steps?: string[] };
  governor_remediation?: string[];
  generated_by?: string;
}

export interface DebugAnalysis {
  summary?: { verdict?: string; parse_health?: string } & Record<string, unknown>;
  parse_health?: DebugParseHealth;
  governor_limit_summary?: unknown[];
  governor_limits?: unknown[];
  transaction_debug_summary?: Record<string, unknown>;
  transactions?: unknown[];
  runtime_components?: unknown[];
  method_summary?: unknown[];
  method_calls?: unknown[];
  errors_and_exceptions?: unknown[];
  warnings?: unknown[];
  soql_usage?: unknown[];
  dml_usage?: unknown[];
  components?: unknown[];
  tool_evidence?: Record<string, unknown>;
  diagnosis?: ({ root_cause?: DebugRootCause } & Record<string, unknown>) | Record<string, unknown>;
  issues?: unknown[];
  call_flow?: unknown[];
  logs?: unknown[];
  [key: string]: unknown;
}

export interface DebugLogRow {
  Id: string;
  Application?: string | null;
  DurationMilliseconds?: number | null;
  Location?: string | null;
  LogLength?: number | null;
  LogUserId?: string | null;
  LogUserName?: string | null;
  LogUsername?: string | null;
  Operation?: string | null;
  Request?: string | null;
  StartTime?: string | null;
  Status?: string | null;
  DisplayName?: string | null;
  [key: string]: unknown;
}

export interface DebugLogBody {
  org_id: string;
  user_id: string;
  log_id: string;
  body: string;
  line_count: number;
  bytes: number;
}

export interface DebugSnapshotFile {
  filename: string;
  bytes: number;
  url: string;
}

export interface DebugSnapshot {
  run_id: string;
  org_id: string;
  user_id: string;
  generated_at: string;
  files: DebugSnapshotFile[];
  summary?: Record<string, unknown>;
}

export interface DebugEvent extends Partial<DebugRunSnapshot> {
  event?: string;
  run_id: string;
  timestamp?: string;
  [key: string]: unknown;
}

function currentIds(): { orgId: string; userId: string } {
  const state = useAuthStoreV1.getState();
  const orgId = resolveCurrentOrgId(state);
  const userId = state.user?.id;
  if (!orgId) throw new Error("Select an organization first.");
  if (!userId) throw new Error("User is not loaded yet.");
  return { orgId, userId };
}

function authHeaders(options: { requireScope?: boolean } = {}): Record<string, string> {
  const requireScope = options.requireScope !== false;
  const headers: Record<string, string> = {};
  const state = useAuthStoreV1.getState();
  const orgId = resolveCurrentOrgId(state);
  const userId = state.user?.id;

  if (requireScope) {
    if (!orgId) throw new Error("Select an organization first.");
    if (!userId) throw new Error("User is not loaded yet.");
  }

  if (orgId) headers["X-Organization-ID"] = orgId;
  if (userId) headers["X-User-ID"] = userId;
  const token = getAuthToken() || readBrowserValue("auth_token") || localDevBearer();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function resolveCurrentOrgId(state: ReturnType<typeof useAuthStoreV1.getState>): string | null {
  if (state.selectedOrg?.id) return state.selectedOrg.id;
  const defaultOrgId = getDefaultOrgId();
  if (defaultOrgId) return defaultOrgId;
  const legacyOrgId = readBrowserValue("org_id");
  if (legacyOrgId) return legacyOrgId;
  return state.organizations.length === 1 ? state.organizations[0].id : null;
}

function readBrowserValue(name: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(name);
    if (stored) return stored;
    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${name}=`));
    return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
  } catch {
    return null;
  }
}

function localDevBearer(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  if (host !== "localhost" && host !== "127.0.0.1") return null;
  return "local-debugging-dev-bearer-token";
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  options: { auth?: boolean; requireScope?: boolean } = {}
): Promise<T> {
  const includeAuth = options.auth !== false;
  const res = await fetch(`${debuggingBaseUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(includeAuth ? authHeaders({ requireScope: options.requireScope }) : {}),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    let detail: unknown = text;
    try {
      detail = JSON.parse(text).detail;
    } catch {
      // Keep raw text below.
    }
    if (isReauthDetail(detail)) {
      throw new Error("Salesforce connection expired. Please reconnect Salesforce or log back in, then retry.");
    }
    // Was `429: llm_budget_exceeded`. The reauth branch above still wins; everything else
    // now reads the same as it does on every other service.
    notifyBudgetExceeded(typeof detail === "string" ? detail : null);
    throw new Error(friendlyHttpMessage({ status: res.status, body: { detail } }));
  }
  return res.json() as Promise<T>;
}

function isReauthDetail(value: unknown): value is { code: string } {
  return Boolean(value && typeof value === "object" && "code" in value && (value as { code?: unknown }).code === "reauth_required");
}

export async function health(): Promise<{ ok: string; service: string }> {
  return request("/health", {}, { requireScope: false });
}

export async function createAnalyzeRun(input: {
  limit: number;
  logIds?: string[];
  includeBodies?: boolean;
}): Promise<DebugRunSnapshot> {
  const { orgId, userId } = currentIds();
  return request(`/debugging/${encodeURIComponent(orgId)}/runs/analyze`, {
    method: "POST",
    body: JSON.stringify({
      user_id: userId,
      limit: input.limit,
      log_ids: input.logIds?.length ? input.logIds : null,
      include_bodies: Boolean(input.includeBodies),
    }),
  });
}

export async function listDebugLogs(input: { limit?: number } = {}): Promise<{ logs: DebugLogRow[]; count: number }> {
  const { orgId, userId } = currentIds();
  return request(
    `/debugging/${encodeURIComponent(orgId)}/logs?user_id=${encodeURIComponent(userId)}&limit=${encodeURIComponent(String(input.limit || 25))}`
  );
}

export async function getDebugLogBody(logId: string): Promise<DebugLogBody> {
  const { orgId, userId } = currentIds();
  return request(
    `/debugging/${encodeURIComponent(orgId)}/logs/${encodeURIComponent(logId)}/body?user_id=${encodeURIComponent(userId)}`
  );
}

export async function createTraceRun(input: {
  salesforceUserId?: string;
  durationMinutes: number;
  pollSeconds: number;
}): Promise<DebugRunSnapshot> {
  const { orgId, userId } = currentIds();
  return request(`/debugging/${encodeURIComponent(orgId)}/runs/trace`, {
    method: "POST",
    body: JSON.stringify({
      user_id: userId,
      salesforce_user_id: input.salesforceUserId?.trim() || null,
      duration_minutes: input.durationMinutes,
      poll_seconds: input.pollSeconds,
    }),
  });
}

export interface SalesforceOrgUser {
  id: string;
  name: string;
  username: string;
}

/** Users who ALREADY have an active Apex TraceFlag (debug logging on) — the source for
 *  the 'whose logs to trace' dropdown. Not an org-wide search: only entities currently
 *  being traced. The chosen user's id is handed to renewTraceFlag to extend their trace. */
export async function listTracedUsers(): Promise<SalesforceOrgUser[]> {
  const { orgId, userId } = currentIds();
  const params = new URLSearchParams({ user_id: userId });
  const res = await request<{ users?: SalesforceOrgUser[] }>(
    `/debugging/${encodeURIComponent(orgId)}/users?${params.toString()}`
  );
  return res.users ?? [];
}

/** Renew (create/extend) the Apex TraceFlag for a user so Salesforce keeps generating
 *  debug logs for them. Does NOT create logs — SF creates them when that user acts; the
 *  UI then reloads and picks from the SF-created logs. */
export async function renewTraceFlag(input: { salesforceUserId?: string; durationMinutes?: number }): Promise<unknown> {
  const { orgId, userId } = currentIds();
  return request(`/debugging/${encodeURIComponent(orgId)}/trace-flags`, {
    method: "POST",
    body: JSON.stringify({
      user_id: userId,
      salesforce_user_id: input.salesforceUserId?.trim() || null,
      duration_minutes: input.durationMinutes ?? 60,
    }),
  });
}

export async function getRun(runId: string): Promise<DebugRunSnapshot> {
  const { orgId, userId } = currentIds();
  return request(
    `/debugging/runs/${encodeURIComponent(runId)}?org_id=${encodeURIComponent(orgId)}&user_id=${encodeURIComponent(userId)}`
  );
}

export async function createDebugSnapshot(runId: string): Promise<DebugSnapshot> {
  const { orgId, userId } = currentIds();
  return request(
    `/debugging/${encodeURIComponent(orgId)}/analysis-runs/${encodeURIComponent(runId)}/snapshot?user_id=${encodeURIComponent(userId)}`,
    { method: "POST" }
  );
}

export function snapshotFileUrl(fileUrl: string): string {
  return `${debuggingBaseUrl}${fileUrl}`;
}

export async function cancelRun(runId: string): Promise<{ ok: boolean; run_id: string }> {
  const { orgId, userId } = currentIds();
  return request(
    `/debugging/runs/${encodeURIComponent(runId)}/cancel?org_id=${encodeURIComponent(orgId)}&user_id=${encodeURIComponent(userId)}`,
    { method: "POST" }
  );
}

async function createStreamTicket(runId: string): Promise<{ ticket: string }> {
  const { orgId, userId } = currentIds();
  return request(
    `/debugging/runs/${encodeURIComponent(runId)}/stream-ticket?org_id=${encodeURIComponent(orgId)}&user_id=${encodeURIComponent(userId)}`,
    { method: "POST" }
  );
}

export function streamRun(args: {
  runId: string;
  onEvent: (event: DebugEvent) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
}): () => void {
  const { orgId, userId } = currentIds();
  const ctrl = new AbortController();

  void (async () => {
    try {
      const { ticket } = await createStreamTicket(args.runId);
      const url =
        `${debuggingBaseUrl}/debugging/runs/${encodeURIComponent(args.runId)}/stream` +
        `?org_id=${encodeURIComponent(orgId)}&user_id=${encodeURIComponent(userId)}&ticket=${encodeURIComponent(ticket)}`;
      const res = await fetch(url, {
        signal: ctrl.signal,
        headers: {
          Accept: "text/event-stream",
          ...authHeaders(),
        },
      });
      if (!res.ok) throw await httpError(res, "stream");
      if (!res.body) throw new Error("stream: no readable body");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const data = frame
            .split("\n")
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trim())
            .join("\n");
          if (!data) continue;
          args.onEvent(JSON.parse(data) as DebugEvent);
        }
      }
      args.onClose?.();
    } catch (error) {
      if ((error as Error).name === "AbortError") return;
      args.onError?.(error as Error);
    }
  })();

  return () => ctrl.abort();
}
